import { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { IconCheck } from '@tabler/icons-react';
import { auth } from '@shared/api/services';
import { stepTransition, stepVariants } from '@shared/lib/onboarding-motion';
import s from './onboarding-layout.module.scss';

export const OnboardingLayout = ({
	steps,
	currentStep,
	onStepChange,
	children,
	footer,
	completedLabel = 'ГОТОВО',
	showStepProgression = false,
}) => {
	const visibleSteps = useMemo(
		() => steps.filter((step) => !step.hidden),
		[steps]
	);

	const stepIndex = visibleSteps.findIndex((step) => step.id === currentStep);
	const progressLabel =
		stepIndex >= 0
			? `ШАГ ${stepIndex + 1} из ${visibleSteps.length}`
			: completedLabel;

	const handleStepClick = (step) => {
		if (step.clickable && onStepChange) {
			onStepChange(step.id);
		}
	};

	const isBefore = (step) => showStepProgression && currentStep > step.id;

	return (
		<div className={s.mainWrapper}>
			<header className={s.header}>
				<div className="container">
					<div className={s.headerInner}>
						<NavLink to="/" className={s.headerLogo}>
							<img
								src={process.env.REACT_APP_STORAGE_URL + 'images/logo-tablet.svg'}
								alt="Созвучно - сервис по поиску преподавателей музыки"
							/>
						</NavLink>
						<button className={s.logout} onClick={() => auth.logout()}>
							Выйти
						</button>
					</div>
				</div>
			</header>

			<div className={s.wrapper}>
				<aside className={s.sidebar}>
					<div className={s.stepsWrapper}>
						<div className={s.stepsLogo}>
							<img
								src={process.env.REACT_APP_STORAGE_URL + 'images/logo.svg'}
								alt="Созвучно - сервис по поиску преподавателей музыки"
							/>
						</div>
						{visibleSteps.map((step) => {
							const StepIcon = step.icon;
							const before = isBefore(step);
							const active = currentStep === step.id;

							return (
								<div
									key={step.id}
									className={classNames(
										s.stepWrapper,
										active && s.active,
										before && s.isBefore,
										step.clickable && s.clickable
									)}
									onClick={() => handleStepClick(step)}
								>
									<div className={s.stepIcon}>
										{StepIcon && (
											<>
												<StepIcon className={s.defaultIcon} />
												{showStepProgression && (
													<IconCheck className={s.checkIcon} />
												)}
											</>
										)}
										{!StepIcon && showStepProgression && (
											<IconCheck className={s.checkIcon} />
										)}
									</div>
									<div className={s.stepContent}>
										<p className={s.stepTitle}>{step.title}</p>
										{step.subtitle && (
											<span className={s.stepSubtitle}>{step.subtitle}</span>
										)}
									</div>
								</div>
							);
						})}
					</div>
					<button className={s.logout} onClick={() => auth.logout()}>
						Выйти
					</button>
				</aside>

				<main className={s.content}>
					<div className={s.stepsCount} aria-live="polite" role="status">
						{progressLabel}
					</div>
					<AnimatePresence mode="wait">
						<motion.div
							key={currentStep}
							className={s.stepMotion}
							variants={stepVariants}
							initial="initial"
							animate="animate"
							exit="exit"
							transition={stepTransition}
							style={{ willChange: 'transform, opacity' }}
						>
							{children}
						</motion.div>
					</AnimatePresence>
				</main>
			</div>

			{footer && <footer className={s.footer}>{footer}</footer>}
		</div>
	);
};
